/**
 * Business logic hook for lesson creation/editing workflow
 * Combines React Query with local state management (no XState)
 * Follows the pattern from waste orders domain
 */

import { useReducer, useCallback, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import { useLessonQuery as useLessonById, useDefaultLessonQuery as useDefaultLesson } from './useLessonsQueries';
import { useLessonMutations } from './use-lesson-mutations';
import { slideRegistry } from '../core/plugin-system';
import { validateLessonComplete } from '../shared/validation';
import { getLessonTypeConfig } from '../config/lesson-types.config';
import type { LessonDTO, SlideDTO, LessonType } from '../core/types';
import type {
  LessonCreationState,
  LessonCreationAction,
  AutoSaveConfig,
} from '../core/types/workflow.types';
import { getErrorMessage } from '@/core/api/response-handler';

// ============================================================================
// REDUCER FOR LESSON STATE
// ============================================================================

const initialState: LessonCreationState = {
  status: 'idle',
  lesson: null,
  selectedSlideId: null,
  isDirty: false,
  errors: {},
  lastSavedAt: null,
};

function lessonCreationReducer(
  state: LessonCreationState,
  action: LessonCreationAction
): LessonCreationState {
  switch (action.type) {
    case 'LOAD_START':
      return { ...state, status: 'loading' };

    case 'LOAD_SUCCESS':
      return {
        ...state,
        status: 'editing',
        lesson: action.payload,
        isDirty: false,
        errors: {},
      };

    case 'LOAD_ERROR':
      return { ...state, status: 'error', errors: { _load: [action.payload] } };

    case 'UPDATE_METADATA':
      return {
        ...state,
        lesson: state.lesson ? { ...state.lesson, ...action.payload } : null,
        isDirty: true,
        errors: {}, // Clear validation errors when user makes changes
      };

    case 'SELECT_SLIDE':
      return { ...state, selectedSlideId: action.payload };

    case 'ADD_SLIDE':
      return {
        ...state,
        lesson: state.lesson
          ? {
              ...state.lesson,
              slides: [...(state.lesson.slides || []), action.payload],
            }
          : null,
        selectedSlideId: action.payload.id || null,
        isDirty: true,
      };

    case 'UPDATE_SLIDE': {
      if (!state.lesson?.slides) return state;
      return {
        ...state,
        lesson: {
          ...state.lesson,
          slides: state.lesson.slides.map((slide) =>
            slide.id === action.payload.slideId
              ? { ...slide, ...action.payload.updates }
              : slide
          ),
        },
        isDirty: true,
      };
    }

    case 'REMOVE_SLIDE': {
      if (!state.lesson?.slides) return state;
      const newSlides = state.lesson.slides.filter((s) => s.id !== action.payload);
      return {
        ...state,
        lesson: { ...state.lesson, slides: newSlides },
        selectedSlideId:
          state.selectedSlideId === action.payload
            ? newSlides[0]?.id || null
            : state.selectedSlideId,
        isDirty: true,
      };
    }

    case 'DUPLICATE_SLIDE': {
      if (!state.lesson?.slides) return state;
      const originalSlide = state.lesson.slides.find((s) => s.id === action.payload);
      if (!originalSlide) return state;

      if (!originalSlide.type) return state;
      const plugin = slideRegistry.get(originalSlide.type);
      if (!plugin) return state;

      const duplicatedSlide = plugin.duplicate(originalSlide);
      const originalIndex = state.lesson.slides.findIndex(
        (s) => s.id === action.payload
      );

      const newSlides = [...state.lesson.slides];
      newSlides.splice(originalIndex + 1, 0, duplicatedSlide);

      return {
        ...state,
        lesson: { ...state.lesson, slides: newSlides },
        selectedSlideId: duplicatedSlide.id || null,
        isDirty: true,
      };
    }

    case 'REORDER_SLIDES':
      return {
        ...state,
        lesson: state.lesson ? { ...state.lesson, slides: action.payload } : null,
        isDirty: true,
      };

    case 'VALIDATE_START':
      return { ...state, status: 'validating' };

    case 'VALIDATE_SUCCESS':
      return { ...state, status: 'editing', errors: {} };

    case 'VALIDATE_ERROR':
      return { ...state, status: 'editing', errors: action.payload };

    case 'SAVE_START':
      return { ...state, status: 'saving' };

    case 'SAVE_SUCCESS':
      return {
        ...state,
        status: 'success',
        lesson: action.payload,
        isDirty: false,
        errors: {},
        lastSavedAt: Date.now(),
      };

    case 'SAVE_ERROR':
      return {
        ...state,
        status: 'editing',
        errors: { _save: [action.payload] },
      };

    case 'PREVIEW_START':
      return { ...state, status: 'previewing' };

    case 'PREVIEW_END':
      return { ...state, status: 'editing' };

    case 'MARK_DIRTY':
      return { ...state, isDirty: true };

    case 'MARK_CLEAN':
      return { ...state, isDirty: false };

    case 'RESET':
      return initialState;

    default:
      return state;
  }
}

// ============================================================================
// MAIN HOOK
// ============================================================================

/**
 * Hook for lesson creation and editing workflow
 * Manages entire lifecycle: load, edit, validate, save
 *
 * @param lessonType - Type of lesson (LESSON, HOMEWORK, etc.)
 * @param lessonId - ID of existing lesson (for edit) or undefined (for create)
 * @param autoSave - Optional auto-save configuration
 *
 * @example
 * const creation = useLessonCreation('LESSON', lessonId);
 * creation.addSlide('QUESTION');
 * creation.updateMetadata({ title: 'New Title' });
 * creation.save();
 */
export function useLessonCreation(
  lessonType: LessonType,
  lessonId?: string,
  autoSave?: AutoSaveConfig
) {
  const navigate = useNavigate();
  const [state, dispatch] = useReducer(lessonCreationReducer, initialState);

  // React Query hooks
  const existingLesson = useLessonById(lessonId!, { enabled: !!lessonId });
  const defaultLesson = useDefaultLesson({ enabled: !lessonId });
  const mutations = useLessonMutations();

  // Lesson type configuration
  const config = getLessonTypeConfig(lessonType as LessonType);

  // Auto-save timer
  const autoSaveTimer = useRef<NodeJS.Timeout>();

  // ========================================================================
  // INITIALIZATION
  // ========================================================================

  useEffect(() => {
    if (lessonId && existingLesson.data) {
      dispatch({ type: 'LOAD_SUCCESS', payload: existingLesson.data });
    } else if (!lessonId && defaultLesson.data) {
      dispatch({
        type: 'LOAD_SUCCESS',
        payload: { ...defaultLesson.data, type: lessonType, slides: [] },
      });
    }
  }, [lessonId, existingLesson.data, defaultLesson.data, lessonType]);

  useEffect(() => {
    if (existingLesson.error || defaultLesson.error) {
      const error = existingLesson.error || defaultLesson.error;
      dispatch({ type: 'LOAD_ERROR', payload: getErrorMessage(error, 'Eroare la încărcare') });
    }
  }, [existingLesson.error, defaultLesson.error]);

  // ========================================================================
  // AUTO-SAVE
  // ========================================================================

  useEffect(() => {
    if (!autoSave?.enabled || !state.isDirty || !state.lesson) {
      return;
    }

    // Clear existing timer
    if (autoSaveTimer.current) {
      clearTimeout(autoSaveTimer.current);
    }

    // Set new timer
    autoSaveTimer.current = setTimeout(() => {
      handleSave(true); // Silent save
    }, autoSave.delayMs);

    return () => {
      if (autoSaveTimer.current) {
        clearTimeout(autoSaveTimer.current);
      }
    };
  }, [state.isDirty, state.lesson, autoSave]);

  // ========================================================================
  // METADATA OPERATIONS
  // ========================================================================

  const updateMetadata = useCallback((updates: Partial<LessonDTO>) => {
    dispatch({ type: 'UPDATE_METADATA', payload: updates });
  }, []);

  // ========================================================================
  // SLIDE OPERATIONS
  // ========================================================================

  const addSlide = useCallback(
    (slideType: string) => {
      // Check if slide type is allowed for this lesson type
      if (!config.features.allowedSlideTypes.includes(slideType)) {
        toast.error(`Tipul de slide "${slideType}" nu este permis pentru ${config.displayName}`);
        return;
      }

      // Check max slides limit
      const currentSlideCount = state.lesson?.slides?.length || 0;
      if (
        config.features.maxSlides &&
        currentSlideCount >= config.features.maxSlides
      ) {
        toast.error(`Numărul maxim de slide-uri (${config.features.maxSlides}) a fost atins`);
        return;
      }

      // Get plugin and create default slide
      const plugin = slideRegistry.get(slideType);
      if (!plugin) {
        toast.error(`Plugin-ul pentru "${slideType}" nu a fost găsit`);
        return;
      }

      const newSlide = plugin.createDefault();
      dispatch({ type: 'ADD_SLIDE', payload: newSlide });
    },
    [config, state.lesson]
  );

  const updateSlide = useCallback((slideId: string, updates: Partial<SlideDTO>) => {
    dispatch({ type: 'UPDATE_SLIDE', payload: { slideId, updates } });
  }, []);

  const removeSlide = useCallback(
    (slideId: string) => {
      const currentSlideCount = state.lesson?.slides?.length || 0;
      if (
        config.features.minSlides &&
        currentSlideCount <= config.features.minSlides
      ) {
        toast.error(`Numărul minim de slide-uri (${config.features.minSlides}) este necesar`);
        return;
      }

      dispatch({ type: 'REMOVE_SLIDE', payload: slideId });
    },
    [config, state.lesson]
  );

  const duplicateSlide = useCallback((slideId: string) => {
    dispatch({ type: 'DUPLICATE_SLIDE', payload: slideId });
  }, []);

  const reorderSlides = useCallback((slides: SlideDTO[]) => {
    dispatch({ type: 'REORDER_SLIDES', payload: slides });
  }, []);

  const selectSlide = useCallback((slideId: string | null) => {
    dispatch({ type: 'SELECT_SLIDE', payload: slideId });
  }, []);

  // ========================================================================
  // VALIDATION & SAVE
  // ========================================================================

  const validate = useCallback(() => {
    if (!state.lesson) return false;

    dispatch({ type: 'VALIDATE_START' });

    const validation = validateLessonComplete(state.lesson);

    if (validation.valid) {
      dispatch({ type: 'VALIDATE_SUCCESS' });
      return true;
    } else {
      dispatch({ type: 'VALIDATE_ERROR', payload: validation.errors });
      return false;
    }
  }, [state.lesson]);

  const handleSave = useCallback(
    async (silent = false) => {
      if (!state.lesson) return;

      // Validate before save
      if (!validate()) {
        if (!silent) {
          toast.error('Lecția conține erori și nu poate fi salvată');
        }
        return;
      }

      dispatch({ type: 'SAVE_START' });

      try {
        let saved: LessonDTO;

        if (state.lesson.id) {
          // Update existing
          saved = await mutations.update.mutateAsync(state.lesson);
        } else {
          // Create new
          saved = await mutations.create.mutateAsync(state.lesson);
        }

        dispatch({ type: 'SAVE_SUCCESS', payload: saved });

        // ✅ Success toast already shown by mutation.onSuccess callback
        // (defined in use-lesson-mutations.ts lines 32 for create, 53 for update)

        if (autoSave?.onSave) {
          autoSave.onSave(saved);
        }

        // Navigate to edit page if this was a create
        // Add small delay to allow user to see success toast before navigation
        if (!lessonId && saved.id) {
          setTimeout(() => {
            navigate(`/lessons/${lessonType.toLowerCase()}/edit/${saved.id}`);
          }, 500);
        }
      } catch (error: unknown) {
        // ✅ Error toast already shown by mutation.onError callback
        // (defined in use-lesson-mutations.ts lines 38-42 for create, 60-63 for update)
        // We just store the error in state for sidebar display
        dispatch({
          type: 'SAVE_ERROR',
          payload: getErrorMessage(error, 'Eroare la salvare'),
        });

        if (autoSave?.onError) {
          autoSave.onError(
            error instanceof Error ? error : new Error(getErrorMessage(error, 'Eroare la salvare'))
          );
        }

        // Don't show additional toast here - mutation already handled it
        // Mutations follow TanStack Query best practice: onSuccess/onError callbacks
      }
    },
    [state.lesson, validate, mutations, lessonId, lessonType, navigate, autoSave]
  );

  // ========================================================================
  // PREVIEW
  // ========================================================================

  const startPreview = useCallback(() => {
    dispatch({ type: 'PREVIEW_START' });
  }, []);

  const endPreview = useCallback(() => {
    dispatch({ type: 'PREVIEW_END' });
  }, []);

  // ========================================================================
  // RETURN API
  // ========================================================================

  return {
    // State
    lesson: state.lesson,
    selectedSlideId: state.selectedSlideId,
    isDirty: state.isDirty,
    errors: state.errors,
    lastSavedAt: state.lastSavedAt,

    // Status flags
    isLoading: state.status === 'loading' || existingLesson.isLoading || defaultLesson.isLoading,
    isEditing: state.status === 'editing',
    isValidating: state.status === 'validating',
    isSaving: state.status === 'saving' || mutations.isLoading,
    isPreviewing: state.status === 'previewing',
    isSuccess: state.status === 'success',
    isError: state.status === 'error',

    // Configuration
    config,

    // Metadata operations
    updateMetadata,

    // Slide operations
    addSlide,
    updateSlide,
    removeSlide,
    duplicateSlide,
    reorderSlides,
    selectSlide,

    // Workflow operations
    validate,
    save: handleSave,
    startPreview,
    endPreview,

    // Utility
    canAddSlide: (slideType: string) =>
      config.features.allowedSlideTypes.includes(slideType),
    getCurrentSlideCount: () => state.lesson?.slides?.length || 0,
    getMaxSlides: () => config.features.maxSlides,
    getMinSlides: () => config.features.minSlides,
  };
}
